import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CheckCircle2, Calendar as CalendarIcon, ArrowRight, Loader2, Check } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { TASK_OUTCOMES, calculateTaskPriority, normalizeTaskType } from '@/components/tasks/TaskStateMachine';
import { Badge } from '@/components/ui/badge';

/**
 * Task Complete Modal
 * Görev tamamlama ve Wiedervorlage (follow-up) oluşturma modalı
 *
 * @param {Object} task - Tamamlanacak görev
 * @param {boolean} open - Modal açık mı
 * @param {function} onOpenChange - Modal durumu değiştiğinde
 * @param {function} onComplete - Tamamlama callback
 * @param {boolean} isLoading - Yükleniyor durumu
 */
export default function TaskCompleteModal({
  task,
  open,
  onOpenChange,
  onComplete,
  isLoading = false,
}) {
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [customDate, setCustomDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Eski kayıtlarda task_type boş olabilir, typ alanını fallback olarak kullan
  // ve alias normalize et (örn. "Befragen" → "Bedarfsabfrage").
  const effectiveType = normalizeTaskType(task?.task_type || task?.typ);
  const knownOutcomes = effectiveType ? (TASK_OUTCOMES[effectiveType] || []) : [];

  // Bilinmeyen tip için generic "manuel kapatma" seçeneği — task'ın hiç
  // kapanmaması yerine kullanıcı manuel kapatabilsin.
  const FALLBACK_OUTCOME = {
    value: 'manual_close',
    label: 'Manuell abschließen (kein Folgetask)',
    nextTask: null,
    nextDays: 0,
  };

  const outcomes = knownOutcomes.length > 0 ? knownOutcomes : [FALLBACK_OUTCOME];
  const isUnknownType = knownOutcomes.length === 0;
  const selectedOutcomeConfig = outcomes.find(o => o.value === selectedOutcome);

  const handleSubmit = () => {
    if (!selectedOutcome) return;

    onComplete({
      task,
      outcome: selectedOutcome,
      notes,
      customDate,
    });
  };

  // Acil/manuel kapatma — herhangi bir seçim olmadan çalışır.
  // task_type bilinmese veya outcome state takılı kalsa bile task'ı kapatır.
  const handleForceClose = () => {
    onComplete({
      task,
      outcome: 'manual_close',
      notes,
      customDate: null,
    });
  };

  const handleClose = () => {
    setSelectedOutcome('');
    setNotes('');
    setCustomDate(null);
    onOpenChange(false);
  };

  const calculateDefaultDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            Aufgabe abschließen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4 px-6 overflow-y-auto flex-1 min-h-0">
          {/* Task Info */}
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <p className="font-medium text-slate-900">{task?.lead_name}</p>
              {task && (() => {
                const priority = calculateTaskPriority(task);
                if (priority >= 100) return <Badge className="bg-red-600 text-white">Überfällig</Badge>;
                if (priority >= 60) return <Badge className="bg-amber-500 text-white">Hoch</Badge>;
                if (priority >= 30) return <Badge className="bg-blue-500 text-white">Mittel</Badge>;
                return <Badge variant="outline">Normal</Badge>;
              })()}
            </div>
            <p className="text-sm text-slate-500">{task?.task_type}</p>
            {task?.info_block && (
              <p className="text-xs text-slate-400 mt-1 border-t pt-1">{task.info_block}</p>
            )}
            {task?.previous_task_id && (
              <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                <ArrowRight className="w-3 h-3" /> Wiedervorlage-Kette
              </p>
            )}
          </div>

          {/* Outcome Selection — plain buttons, RadioGroup'tan daha güvenilir */}
          <div className="space-y-2">
            <Label>Ergebnis *</Label>
            {isUnknownType && (
              <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <span className="font-semibold">Hinweis:</span>
                <span>
                  Aufgabentyp <strong>{effectiveType || 'unbekannt'}</strong> hat keine vordefinierten Ergebnisse. Sie können diese Aufgabe manuell abschließen.
                </span>
              </div>
            )}
            <div className="space-y-2">
              {outcomes.map((outcome) => {
                const isSelected = selectedOutcome === outcome.value;
                return (
                  <button
                    type="button"
                    key={outcome.value}
                    onClick={() => setSelectedOutcome(outcome.value)}
                    className={cn(
                      'w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors min-h-[56px]',
                      isSelected
                        ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20'
                        : 'border-slate-200 hover:bg-slate-50 active:bg-slate-100'
                    )}
                  >
                    <span
                      className={cn(
                        'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5',
                        isSelected ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300 bg-white'
                      )}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </span>
                    <span className="flex-1">
                      <span className="block font-medium text-slate-900">{outcome.label}</span>
                      {outcome.nextTask && (
                        <span className="block text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          <ArrowRight className="w-3 h-3" />
                          Nächste Aufgabe: {outcome.nextTask} in {outcome.nextDays} Tag(en)
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3-Bereich Auswirkung */}
          {selectedOutcomeConfig && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1.5">
              <p className="text-xs font-semibold text-blue-700 mb-1">Auswirkungen:</p>
              <p className="text-xs text-blue-600 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                {selectedOutcomeConfig.nextTask
                  ? `Task: Wiedervorlage "${selectedOutcomeConfig.nextTask}" wird erstellt`
                  : 'Task: Wird abgeschlossen (keine Wiedervorlage)'}
              </p>
              {task?.lead_id && (
                <>
                  <p className="text-xs text-blue-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    {['interested', 'order', 'accepted', 'not_interested', 'rejected', 'lost_customer'].includes(selectedOutcomeConfig.value)
                      ? 'Lead: Status wird aktualisiert'
                      : 'Lead: Status bleibt unverändert'}
                  </p>
                  <p className="text-xs text-blue-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 inline-block" />
                    Lead: Score wird berechnet
                  </p>
                </>
              )}
            </div>
          )}

          {/* Custom Date for Follow-up */}
          {selectedOutcomeConfig?.nextTask && (
            <div className="space-y-2">
              <Label>Wiedervorlage Datum</Label>
              <div className="flex gap-2">
                <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !customDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDate
                        ? format(customDate, 'dd. MMMM yyyy', { locale: de })
                        : `Standard: ${format(
                            calculateDefaultDate(selectedOutcomeConfig.nextDays),
                            'dd. MMM',
                            { locale: de }
                          )}`}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customDate}
                      onSelect={(date) => {
                        setCustomDate(date);
                        setShowDatePicker(false);
                      }}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {customDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCustomDate(null)}
                  >
                    Reset
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Zusätzliche Informationen..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 flex-shrink-0 px-6 pb-6 pt-4 border-t border-slate-100 bg-white flex-col sm:flex-row">
          <Button
            variant="ghost"
            onClick={handleForceClose}
            disabled={isLoading}
            className="min-h-[44px] text-slate-500 hover:text-slate-900 sm:mr-auto"
            title="Aufgabe ohne Folgetask sofort abschließen"
          >
            Sofort schließen
          </Button>
          <Button variant="outline" onClick={handleClose} disabled={isLoading} className="min-h-[44px]">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedOutcome || isLoading}
            className="bg-emerald-600 hover:bg-emerald-700 min-h-[44px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Wird gespeichert...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Abschließen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
